/**
 * PartDrawer.jsx - one spare part: every field (editable with Spare Parts
 * Edit), "Price checked today", the robots whose recommended list names it,
 * its stock rows and every stock move. `partId` null = a new part.
 *
 * Saving sends only the fields that changed. A changed STANDARD or GLOBAL
 * price is stamped "checked today" by the server unless the date was changed
 * too; "Price checked today" stamps it with the prices as they are (or as
 * just typed) - FANUC's prices did not move, the check still counts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import { checkPrice, createPart, deletePart, errorOf, getPart, updatePart } from './sparePartsApi';
import { fmtDay, fmtMoney, fmtPct } from './sparePartsRules';
import { Fact, GREEN, LINE, MUTED, Msg, PriceChip, num, td, th } from './spUi';
import { Btn, Field, Grid, NumInput, SelectInput, TextInput, inputStyle } from '../quotes/quoteUi';

const FIELDS = [
  'part_number',
  'description',
  'part_type',
  'manufacturer',
  'vendor',
  'standard_price',
  'global_price',
  'currency',
  'price_checked_at',
  'price_source',
  'country_of_origin',
  'eccn',
  'htsus',
  'notes',
];
const PRICE_FIELDS = ['standard_price', 'global_price'];

const draftOf = (p) => Object.fromEntries(FIELDS.map((k) => [k, p && p[k] !== null && p[k] !== undefined ? String(p[k]) : '']));
const BLANK = { ...draftOf(null), part_type: 'part', manufacturer: 'FANUC', currency: 'USD' };

/** The fields that differ from what is saved, ready to send. */
function changedFields(draft, saved) {
  const out = {};
  for (const k of FIELDS) {
    const a = String(draft[k] ?? '').trim();
    const b = String(saved[k] ?? '').trim();
    const same = PRICE_FIELDS.includes(k) ? (a === '' && b === '') || (a !== '' && b !== '' && Number(a) === Number(b)) : a === b;
    if (!same) out[k] = a === '' ? null : a;
  }
  return out;
}

export default function PartDrawer({ partId, editable, onClose, onOpened, onOpenRobot, onChanged = () => {} }) {
  const isNew = partId === null || partId === undefined;
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(isNew ? BLANK : null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const reads = useRef(latestReads()).current;
  const saved = useMemo(() => (data ? draftOf(data.part) : BLANK), [data]);
  const dirty = draft ? Object.keys(changedFields(draft, saved)).length > 0 : false;
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (isNew) return;
      const ticket = reads.start();
      const res = await getPart(partId);
      if (!reads.show(ticket)) return;
      if (!res.ok) {
        if (!quiet) setMsg({ kind: 'error', text: errorOf(res, 'Could not load the part.') });
        return;
      }
      setData(res.body);
      // someone else's save shows at once - unless this person is mid-edit
      if (!dirtyRef.current || !quiet) setDraft(draftOf(res.body.part));
    },
    [isNew, partId, reads]
  );
  useEffect(() => {
    load();
  }, [load]);
  useLiveRefresh('spare-parts', () => load({ quiet: true }));

  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const ro = !editable;
  const part = data && data.part;

  const save = async () => {
    setBusy(true);
    const patch = changedFields(draft, saved);
    const res = isNew ? await createPart(patch) : await updatePart(part.id, patch);
    setBusy(false);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not save the part.') });
      return;
    }
    if (isNew) {
      onOpened(res.body.id);
      return;
    }
    onChanged();
    setMsg({
      kind: 'ok',
      text:
        res.body.price_checked_at !== (part && part.price_checked_at)
          ? `Saved. Price checked ${fmtDay(res.body.price_checked_at)}.`
          : 'Saved.',
    });
    await load();
  };
  const priceChecked = async () => {
    setBusy(true);
    const typed = changedFields(draft, saved);
    const body = {};
    for (const k of PRICE_FIELDS) if (k in typed) body[k] = typed[k];
    if ('price_source' in typed) body.price_source = typed.price_source;
    const res = await checkPrice(part.id, body);
    setBusy(false);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not record the price check.') });
      return;
    }
    onChanged();
    setMsg({ kind: 'ok', text: `Price checked ${fmtDay(res.body.price_checked_at)}.` });
    await load();
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${part.part_number} from the catalog?`)) return;
    const res = await deletePart(part.id);
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not delete the part.') });
      return;
    }
    onChanged();
    onClose();
  };

  const title = isNew ? 'New spare part' : part ? `${part.part_number}${part.description ? ` - ${part.description}` : ''}` : 'Spare part';
  const footer =
    editable && draft ? (
      <>
        {!isNew && part && (
          <Btn danger onClick={remove} disabled={busy} style={{ marginRight: 'auto' }}>
            Delete part
          </Btn>
        )}
        {!isNew && part && (
          <Btn onClick={priceChecked} disabled={busy} title="FANUC's prices were checked today - stamps today's date with the prices shown">
            Price checked today
          </Btn>
        )}
        {dirty && !isNew && (
          <Btn
            onClick={() => {
              setDraft(saved);
              setMsg(null);
            }}
            disabled={busy}
          >
            Undo changes
          </Btn>
        )}
        <Btn primary onClick={save} disabled={busy || !dirty || !String(draft.part_number).trim()}>
          {busy ? 'Saving...' : isNew ? 'Add part' : 'Save'}
        </Btn>
      </>
    ) : null;

  return (
    <Modal title={title} onClose={onClose} width={980} footer={footer}>
      <Msg msg={msg} />
      {!draft && <div style={{ padding: 20, color: MUTED, fontSize: 12 }}>Loading the part...</div>}
      {draft && (
        <>
          {part && (
            <div
              style={{
                display: 'flex',
                gap: 18,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: 12,
                padding: '10px 12px',
                background: '#FAFBF7',
                border: `1px solid ${LINE}`,
                borderRadius: 8,
              }}
            >
              <Fact label="Standard (list)">{fmtMoney(part.standard_price, part.currency)}</Fact>
              <Fact label="GLOBAL price">
                <b>{fmtMoney(part.global_price, part.currency)}</b>
              </Fact>
              <Fact label="Discount">{fmtPct(part.discount_pct)}</Fact>
              <Fact label="Price checked">
                <PriceChip age={part.price_age} checkedAt={part.price_checked_at} />
              </Fact>
              <Fact label="On hand">
                <b>{part.on_hand}</b>
              </Fact>
              <Fact label="On robot lists">{part.robot_count}</Fact>
            </div>
          )}
          <Grid cols={4}>
            <Field label="Part number" span={1}>
              <TextInput aria-label="Part number" value={draft.part_number} disabled={ro} onChange={set('part_number')} />
            </Field>
            <Field label="Description" span={3}>
              <TextInput aria-label="Description" value={draft.description} disabled={ro} onChange={set('description')} />
            </Field>
            <Field label="Type">
              <SelectInput
                aria-label="Type"
                value={draft.part_type}
                disabled={ro}
                onChange={set('part_type')}
                options={[
                  { value: 'part', label: 'Spare part' },
                  { value: 'robot', label: 'Robot configuration' },
                ]}
              />
            </Field>
            <Field label="Manufacturer">
              <TextInput aria-label="Manufacturer" value={draft.manufacturer} disabled={ro} onChange={set('manufacturer')} />
            </Field>
            <Field label="Vendor (blank = manufacturer)">
              <TextInput aria-label="Vendor" value={draft.vendor} disabled={ro} onChange={set('vendor')} />
            </Field>
            <Field label="Currency">
              <TextInput
                aria-label="Currency"
                value={draft.currency}
                disabled={ro}
                onChange={(v) => set('currency')(v.toUpperCase().slice(0, 3))}
              />
            </Field>
            <Field label="Standard price (FANUC list)">
              <NumInput aria-label="Standard price" value={draft.standard_price} disabled={ro} onChange={set('standard_price')} />
            </Field>
            <Field label="GLOBAL price">
              <NumInput aria-label="GLOBAL price" value={draft.global_price} disabled={ro} onChange={set('global_price')} />
            </Field>
            <Field label="Price checked on" title="Stamped today when a price changes; set it to the day FANUC was read">
              <TextInput
                type="date"
                aria-label="Price checked on"
                value={draft.price_checked_at}
                disabled={ro}
                onChange={set('price_checked_at')}
              />
            </Field>
            <Field label="Price source">
              <TextInput
                aria-label="Price source"
                value={draft.price_source}
                disabled={ro}
                onChange={set('price_source')}
                placeholder="FANUC CRC, quote #..."
              />
            </Field>
            <Field label="Country of origin">
              <TextInput aria-label="Country of origin" value={draft.country_of_origin} disabled={ro} onChange={set('country_of_origin')} />
            </Field>
            <Field label="ECCN">
              <TextInput aria-label="ECCN" value={draft.eccn} disabled={ro} onChange={set('eccn')} placeholder="EAR99" />
            </Field>
            <Field label="HTSUS">
              <TextInput aria-label="HTSUS" value={draft.htsus} disabled={ro} onChange={set('htsus')} placeholder="8536.10.0040" />
            </Field>
            <div />
            <Field label="Notes" span={4}>
              <textarea
                aria-label="Notes"
                value={draft.notes}
                disabled={ro}
                onChange={(e) => set('notes')(e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', ...(ro ? { background: '#F4F5F2' } : null) }}
              />
            </Field>
          </Grid>

          {data && (
            <>
              <Section title={`Robots that list it (${data.robots.length})`}>
                {data.robots.length ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Robots that list this part">
                    <thead>
                      <tr>
                        {['F-number', 'Model', 'Customer', 'Project #', 'Recommended qty', 'Category'].map((h) => (
                          <th key={h} style={{ ...th, position: 'static' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.robots.map((r) => (
                        <tr key={r.line_id}>
                          <td style={td}>
                            <button
                              type="button"
                              onClick={() => onOpenRobot(r.id)}
                              style={{ all: 'unset', cursor: 'pointer', color: GREEN, fontWeight: 700 }}
                            >
                              {r.f_number}
                            </button>
                          </td>
                          <td style={td}>{r.model || '-'}</td>
                          <td style={td}>{r.customer || '-'}</td>
                          <td style={td}>{r.project_number || '-'}</td>
                          <td style={num}>{r.recommended_qty}</td>
                          <td style={td}>{r.category || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <Empty>No robot's recommended list names this part yet.</Empty>
                )}
              </Section>
              <Section title={`Stock (${data.inventory.reduce((t, i) => t + i.qty_on_hand, 0)} on hand)`}>
                {data.inventory.length ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Stock of this part">
                    <thead>
                      <tr>
                        {['Location', 'Condition', 'On hand', 'Unit cost', 'Value', 'Bought for', 'Received'].map((h) => (
                          <th key={h} style={{ ...th, position: 'static' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.inventory.map((i) => (
                        <tr key={i.id}>
                          <td style={td}>{i.location || '-'}</td>
                          <td style={td}>{i.condition}</td>
                          <td style={num}>{i.qty_on_hand}</td>
                          <td style={num}>{fmtMoney(i.unit_cost, i.currency)}</td>
                          <td style={num}>{fmtMoney(i.value, i.currency)}</td>
                          <td style={td}>
                            {[i.origin_project_number, i.project_quote ? `Q${i.project_quote}` : null].filter(Boolean).join(' · ') || '-'}
                          </td>
                          <td style={td}>{fmtDay(i.received_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <Empty>None on the shelf. Add stock on the Inventory tab.</Empty>
                )}
              </Section>
              <Section title="Stock moves">
                {data.moves.length ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Stock moves of this part">
                    <thead>
                      <tr>
                        {['When', 'Move', 'Qty', 'Left', 'Why', 'Project #', 'By'].map((h) => (
                          <th key={h} style={{ ...th, position: 'static' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.moves.map((m) => (
                        <tr key={m.id}>
                          <td style={td}>
                            {new Date(m.moved_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </td>
                          <td style={{ ...td, fontWeight: 700, color: m.direction === 'in' ? GREEN : '#B71C1C' }}>
                            {m.direction === 'in' ? 'In' : 'Out'}
                          </td>
                          <td style={num}>{m.qty}</td>
                          <td style={num}>{m.qty_after}</td>
                          <td style={td}>{m.reason}</td>
                          <td style={td}>{m.project_number || '-'}</td>
                          <td style={td}>{m.moved_by || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <Empty>No stock has moved yet.</Empty>
                )}
              </Section>
            </>
          )}
        </>
      )}
    </Modal>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginTop: 16 }}>
      <h4 style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 800, color: GREEN, letterSpacing: 0.5, textTransform: 'uppercase' }}>
        {title}
      </h4>
      {children}
    </section>
  );
}
function Empty({ children }) {
  return <div style={{ fontSize: 11.5, color: MUTED, padding: '6px 2px' }}>{children}</div>;
}
